import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Seo from "@/components/feature/Seo";
import AnimeCard from "@/components/feature/AnimeCard";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import { animeList, genreOptions } from "@/mocks/anime";

const GENRE_ICONS: Record<string, string> = {
  热血: "ri-fire-line",
  恋爱: "ri-heart-3-line",
  科幻: "ri-rocket-2-line",
  经典: "ri-award-line",
  奇幻: "ri-magic-line",
  校园: "ri-school-line",
  治愈: "ri-plant-line",
  悬疑: "ri-question-line",
};

function formatViews(views: number) {
  if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
  return `${views}`;
}

export default function Home() {
  const featured = animeList.filter((item) => item.featured);
  const latest = [...animeList]
    .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
    .slice(0, 8);
  const ranking = [...animeList].sort((a, b) => b.views - a.views).slice(0, 8);
  const quickGenres = genreOptions.filter((genre) => genre !== "全部");

  return (
    <Layout>
      <Seo
        title="樱花动漫—免费在线观看动漫完整版平台"
        description="樱花动漫提供丰富的正版动漫内容，涵盖热门动画和经典作品，支持在线观看完整版，有助于动漫爱好者畅享优质视听体验。"
        keywords="樱花动漫,动漫在线观看,完整版动漫,动画视频,动漫平台,动漫资源,动画片"
        path="/"
      />

      <HeroCarousel items={featured} />

      <div className="w-full px-4 md:px-6 lg:px-10">
        {/* 分类快捷入口 */}
        <section className="pt-12 md:pt-16">
          <div className="flex items-end justify-between">
            <div>
              <h2 className="text-xl font-bold text-foreground-950 md:text-2xl">按题材发现</h2>
              <p className="mt-1.5 text-sm text-foreground-500">从你最爱的题材出发，找到下一部心动之作</p>
            </div>
            <Link
              to="/category/all"
              className="hidden items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 hover:text-primary-700 sm:flex"
            >
              全部分类
              <i className="ri-arrow-right-s-line text-lg"></i>
            </Link>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8">
            {quickGenres.map((genre) => (
              <Link
                key={genre}
                to={`/category/${encodeURIComponent(genre)}`}
                className="group flex flex-col items-center gap-2.5 rounded-lg border border-background-200 bg-background-100 px-3 py-5 transition-all hover:-translate-y-1 hover:border-primary-300 hover:bg-primary-50"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-2xl text-primary-600 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
                  <i className={GENRE_ICONS[genre] || "ri-movie-2-line"}></i>
                </span>
                <span className="text-sm font-medium text-foreground-800">{genre}</span>
              </Link>
            ))}
          </div>
        </section>

        {/* 最新更新 */}
        <section className="pt-12 md:pt-16">
          <div className="flex items-end justify-between">
            <div>
              <h2 className="flex items-center gap-2 text-xl font-bold text-foreground-950 md:text-2xl">
                <i className="ri-time-line text-primary-500"></i>
                最新更新
              </h2>
              <p className="mt-1.5 text-sm text-foreground-500">追番日历，第一时间看到新集上线</p>
            </div>
            <Link
              to="/category/all"
              className="flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              查看全部
              <i className="ri-arrow-right-s-line text-lg"></i>
            </Link>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-product-shop>
            {latest.map((anime) => (
              <AnimeCard key={anime.id} anime={anime} />
            ))}
          </div>
        </section>

        {/* 热门排行 */}
        <section className="pt-12 md:pt-16">
          <div className="flex items-end justify-between">
            <div>
              <h2 className="flex items-center gap-2 text-xl font-bold text-foreground-950 md:text-2xl">
                <i className="ri-fire-line text-accent-500"></i>
                热门排行榜
              </h2>
              <p className="mt-1.5 text-sm text-foreground-500">根据全站播放热度实时更新</p>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-x-8 gap-y-3 lg:grid-cols-2">
            {ranking.map((anime, index) => (
              <Link
                key={anime.id}
                to={`/detail/${anime.id}`}
                className="group flex items-center gap-4 rounded-lg border border-background-200 bg-background-100 p-3 transition-all hover:border-primary-300 hover:bg-primary-50"
              >
                <span
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-sm font-bold ${
                    index < 3
                      ? "bg-accent-500 text-background-50"
                      : "bg-background-200 text-foreground-600"
                  }`}
                >
                  {index + 1}
                </span>
                <div className="relative h-16 w-12 shrink-0 overflow-hidden rounded-md bg-background-200">
                  <img
                    src={anime.cover}
                    alt={`${anime.title} 封面`}
                    title={`${anime.title} 在线观看 - 樱花动漫`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="truncate text-sm font-medium text-foreground-950 group-hover:text-primary-600">
                    {anime.title}
                  </h3>
                  <p className="mt-1 truncate text-xs text-foreground-500">
                    {anime.genres.join(" / ")} · {anime.year} · {anime.region}
                  </p>
                </div>
                <span className="hidden shrink-0 items-center gap-1 text-xs text-foreground-500 sm:flex">
                  <i className="ri-play-circle-line text-base text-accent-500"></i>
                  {formatViews(anime.views)}
                </span>
              </Link>
            ))}
          </div>
        </section>

        {/* 网站介绍 */}
        <section className="py-12 md:py-16">
          <div className="rounded-lg border border-background-200 bg-secondary-50 p-6 md:p-10">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
              <div className="lg:col-span-1">
                <h2 className="text-xl font-bold text-foreground-950 md:text-2xl">关于樱花动漫</h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  樱花动漫是一个专注动漫在线观看的内容平台，汇聚热门新番与经久不衰的经典作品。我们坚持正版合规的内容运营理念，让每一次观看都安心、清晰、流畅。
                </p>
                <Link
                  to="/about"
                  className="mt-5 inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  了解更多
                  <i className="ri-arrow-right-s-line text-lg"></i>
                </Link>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 lg:col-span-2">
                <div className="rounded-lg bg-background-50 p-5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 text-xl text-primary-600">
                    <i className="ri-shield-check-line"></i>
                  </span>
                  <h3 className="mt-3 text-sm font-semibold text-foreground-950">正版合规</h3>
                  <p className="mt-1.5 text-xs leading-relaxed text-foreground-500">尊重创作者权益，内容运营全程遵循版权规范。</p>
                </div>
                <div className="rounded-lg bg-background-50 p-5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-100 text-xl text-accent-600">
                    <i className="ri-database-2-line"></i>
                  </span>
                  <h3 className="mt-3 text-sm font-semibold text-foreground-950">资源丰富</h3>
                  <p className="mt-1.5 text-xs leading-relaxed text-foreground-500">覆盖热血、恋爱、科幻、经典等多元题材。</p>
                </div>
                <div className="rounded-lg bg-background-50 p-5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary-100 text-xl text-secondary-700">
                    <i className="ri-refresh-line"></i>
                  </span>
                  <h3 className="mt-3 text-sm font-semibold text-foreground-950">更新及时</h3>
                  <p className="mt-1.5 text-xs leading-relaxed text-foreground-500">新番同步追踪，更新进度一目了然。</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}